import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IconArrowRightBold, Text } from '@croquiscom-pvt/zds';
import styled from '@emotion/styled';

import { useActionOverlayPosition } from './hooks/useActionOverlayPosition';
import { DataRows } from './table/DataRows';
import { MySizeRow } from './table/MySizeRow';
import { CompareButton, StyledTable, TableContainer, TableHeaderCell, TableInner } from './table/styles';

const FIRST_COLUMN_MIN_WIDTH = 88;
const FIRST_COLUMN_MAX_WIDTH = 100;
const FIRST_COLUMN_PADDING = 32; // 좌우 패딩 16px * 2
const OTHER_COLUMN_MIN_WIDTH = 68;

/** 텍스트 너비 측정 (canvas 사용) */
const measureTextWidth = (text: string, font = '600 13px sans-serif'): number => {
  if (typeof document === 'undefined') return 0;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return 0;
  ctx.font = font;
  return ctx.measureText(text).width;
};

/** 첫 열 너비 계산 (88px ~ 100px) */
const calculateFirstColumnWidth = (texts: string[]): number => {
  const maxTextWidth = Math.max(...texts.map(text => measureTextWidth(text)));
  const widthWithPadding = maxTextWidth + FIRST_COLUMN_PADDING;
  return Math.min(FIRST_COLUMN_MAX_WIDTH, Math.max(FIRST_COLUMN_MIN_WIDTH, widthWithPadding));
};

interface SizeMeasurementsTableProps {
  headers: string[];
  rows: string[][];
  fixedColumnWidth: string;
  columnMinWidth?: string;
  handleSetColRef: (cell: string, ref: HTMLSpanElement | null) => void;
  mySizeRow?: {
    label: string;
    actionLabel: string;
    onAction?: () => void;
    values?: string[];
    matchedFlags?: boolean[];
  };
  compareRowImage?: string;
  highlightFlags?: boolean[][];
  hasCompareRow?: boolean;
  onCompareClick?: () => void;
}

export const SizeMeasurementsTable = ({
  headers,
  rows,
  fixedColumnWidth,
  columnMinWidth = '84px',
  handleSetColRef,
  mySizeRow,
  compareRowImage,
  highlightFlags,
  hasCompareRow = false,
  onCompareClick,
}: SizeMeasurementsTableProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const actionCellRef = useRef<HTMLTableCellElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const showActionOverlay = !!mySizeRow && !mySizeRow.values;

  const overlayStyle = useActionOverlayPosition({
    containerRef,
    actionCellRef,
    fixedColumnWidth,
    enabled: showActionOverlay,
  });

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // 첫 열 텍스트들 수집 (헤더 + 각 행의 첫 번째 값 + mySizeRow label)
  const firstColumnTexts = [
    headers[0],
    ...rows.map(row => row[0]),
    ...(mySizeRow?.label ? [mySizeRow.label] : []),
  ];
  const firstColumnWidth = calculateFirstColumnWidth(firstColumnTexts);

  const otherColumnsCount = headers.length - 1;
  const totalMinWidth = firstColumnWidth + otherColumnsCount * OTHER_COLUMN_MIN_WIDTH;
  const isScrollNeeded = totalMinWidth > containerWidth;
  const calculatedColumnWidth = isScrollNeeded ? `${OTHER_COLUMN_MIN_WIDTH}px` : 'auto';

  return (
    <TableWrapper>
      <TableContainer ref={containerRef}>
        <TableInner>
          <StyledTable>
            <thead>
              <tr>
                {headers.map((header, index) => (
                  <TableHeaderCell
                    key={header}
                    width={index === 0 ? `${firstColumnWidth}px` : calculatedColumnWidth}
                    isSticky={index === 0}
                    isCompareRow={!!hasCompareRow}
                    hasActionCell={!!showActionOverlay}
                  >
                    {header}
                  </TableHeaderCell>
                ))}
              </tr>
            </thead>
            <tbody>
              {mySizeRow && (
                <MySizeRow
                  label={mySizeRow.label}
                  onAction={mySizeRow.onAction}
                  values={mySizeRow.values}
                  fixedColumnWidth={`${firstColumnWidth}px`}
                  columnMinWidth={calculatedColumnWidth}
                  colSpan={headers.length - 1}
                  matchedFlags={mySizeRow.matchedFlags}
                  compareRowImage={compareRowImage}
                  actionCellRef={actionCellRef}
                />
              )}
              <DataRows
                rows={rows}
                hasCompareRow={!!hasCompareRow}
                highlightFlags={highlightFlags}
                fixedColumnWidth={`${firstColumnWidth}px`}
                columnMinWidth={calculatedColumnWidth}
                compareRowImage={compareRowImage}
                onCompareClick={onCompareClick}
              />
            </tbody>
          </StyledTable>
        </TableInner>
      </TableContainer>

      {showActionOverlay && overlayStyle && (
        <ActionOverlay
          style={{
            top: overlayStyle.top,
            left: overlayStyle.left,
            height: overlayStyle.height,
          }}
        >
          {mySizeRow.onAction ? (
            <CompareButton onClick={mySizeRow.onAction}>
              {mySizeRow.actionLabel}
              <IconArrowRightBold width={16} height={16} color='subSkyBluePrimary' />
            </CompareButton>
          ) : (
            <Text variant='Caption_12_Medium' color='textSecondary'>
              {mySizeRow.actionLabel}
            </Text>
          )}
        </ActionOverlay>
      )}
    </TableWrapper>
  );
};

const TableWrapper = styled.div`
  position: relative;
`;

const ActionOverlay = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  pointer-events: auto;
  z-index: 3;
`;
